import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Languages } from "lucide-react";
import { DOCTOR } from "@/data/content";
import { useLang, LANGUAGES } from "@/i18n/LanguageContext";

const LINKS = [
  { to: "/", key: "nav_home" },
  { to: "/about", key: "nav_about" },
  { to: "/treatments", key: "nav_treatments" },
  { to: "/conditions", key: "nav_guides" },
  { to: "/contact", key: "nav_contact" },
];

const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 border-b transition-[background-color,box-shadow] duration-500 bg-white/80 backdrop-blur-xl ${
        scrolled ? "border-pine-900/10 shadow-[0_8px_32px_rgba(11,47,47,0.08)]" : "border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3">
          <span className="w-9 h-9 bg-pine text-bone flex items-center justify-center font-serif text-xl leading-none transition-colors duration-300 group-hover:bg-pine-500">
            +
          </span>
          <span className="leading-tight">
            <span className="block font-serif text-lg font-semibold tracking-tight text-pine-950 whitespace-nowrap">
              Dr. Lokesh Naik G
            </span>
            <span className="block text-[9px] uppercase tracking-[0.12em] text-sage max-w-[240px] leading-[1.4]">
              {t("brand_tag")}
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7" data-testid="nav-desktop">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.key.replace("nav_", "")}`}
              className={({ isActive }) =>
                `relative text-sm font-medium tracking-wide transition-colors duration-300 hover:text-pine-500 after:absolute after:-bottom-1.5 after:left-0 after:h-px after:bg-pine-500 after:transition-[width] after:duration-300 ${
                  isActive ? "text-pine-500 after:w-full" : "text-pine-900 after:w-0"
                }`
              }
            >
              {t(l.key)}
            </NavLink>
          ))}
          <div className="relative" data-testid="lang-switcher">
            <Languages size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-pine-500 pointer-events-none" />
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              data-testid="lang-select"
              aria-label="Choose language"
              className="appearance-none bg-transparent border border-pine-900/20 rounded-full pl-8 pr-3 py-2 text-sm font-medium text-pine-900 hover:border-pine-500 cursor-pointer focus:outline-none"
            >
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code} data-testid={`lang-option-${l.code}`}>
                  {l.native}
                </option>
              ))}
            </select>
          </div>
          <a
            href={DOCTOR.phoneHref}
            data-testid="nav-call-link"
            className="hidden xl:flex text-sm font-medium text-pine-900 hover:text-pine-500 transition-colors duration-300 items-center gap-2 whitespace-nowrap"
          >
            <Phone size={14} strokeWidth={2.2} />
            {DOCTOR.phoneDisplay}
          </a>
          <Link
            to="/book"
            data-testid="nav-book-button"
            className="bg-pine text-bone text-sm font-semibold tracking-wide px-6 py-3 rounded-full hover:bg-pine-500 transition-colors duration-300 whitespace-nowrap"
          >
            {t("nav_book")}
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          aria-label="Toggle navigation menu"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-pine-950 p-2"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-pine-900/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.key.replace("nav_", "")}`}
                  className="font-serif text-2xl text-pine-950"
                >
                  {t(l.key)}
                </NavLink>
              ))}
              <div className="flex flex-wrap gap-2 pt-1" data-testid="lang-switcher-mobile">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    data-testid={`lang-mobile-${l.code}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors duration-300 ${
                      lang === l.code
                        ? "bg-pine text-bone border-pine"
                        : "border-pine-900/20 text-pine-900"
                    }`}
                  >
                    {l.native}
                  </button>
                ))}
              </div>
              <Link
                to="/book"
                data-testid="nav-mobile-book-button"
                className="bg-pine text-bone text-center font-semibold px-6 py-4 rounded-full mt-2"
              >
                {t("nav_book")}
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
